"use client";

import React from "react";

import { useTranslations } from "next-intl";

import { Card } from "@/components/ui/Card";
import { usePropertyHistory } from "@/hooks/usePropertyHistory";
import Loader from "@/ui/Loader";

import { TimelineEvent } from "./TimelineEvent";

interface HistoryEvent {
  _id: string;
  propertyId: string;
  userId: {
    name: string;
    role?: string;
  };
  action: string;
  description?: string;
  category: string;
  timestamp: string;
  fieldName?: string;
  oldValue?: any;
  newValue?: any;
}

export interface PropertyTimelineProps {
  propertyId: string;
  events?: HistoryEvent[];
  isLoading?: boolean;
}

export const PropertyTimeline: React.FC<PropertyTimelineProps> = ({
  propertyId,
  events = [],
  isLoading = false,
}) => {
  const t = useTranslations();
  const tRealtor = useTranslations("realtor");
  const { data, isLoading: isQueryLoading } = usePropertyHistory(propertyId);

  const resolvedEvents: HistoryEvent[] = React.useMemo(() => {
    if (events && events.length > 0) return events;
    const apiData = data?.data || [];
    return apiData.map((e: any) => ({
      _id: e._id,
      propertyId: e.propertyId,
      userId: {
        name: e.userName || e.userId?.name || t("User"),
        role: e.userRole,
      },
      action: e.action,
      description: e.fieldName,
      category: e.category,
      timestamp: e.timestamp,
      fieldName: e.fieldName,
      oldValue: e.oldValue,
      newValue: e.newValue,
    }));
  }, [events, data, t]);

  if (isLoading || isQueryLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader />
        <p
          className="ml-4 text-[16px] font-normal text-[#444444]"
          style={{ fontFamily: "Geometria" }}
        >
          {tRealtor("timelineLoadingHistory")}
        </p>
      </div>
    );
  }

  if (resolvedEvents.length === 0) {
    return (
      <div className="text-center py-8">
        <p
          className="text-[16px] font-normal text-[#444444]"
          style={{ fontFamily: "Geometria" }}
        >
          {tRealtor("timelineHistoryEmpty")}
        </p>
      </div>
    );
  }

  return (
    <section>
      <h2 className={"object-detail__section-header mb-12"}>
        {tRealtor("history.title")}
      </h2>
      <Card className="p-0">
        <div className="px-10 py-6">
          {resolvedEvents.map((event, index) => (
            <div key={event._id}>
              <TimelineEvent
                event={event as any}
                isLast={index === resolvedEvents.length - 1}
              />
            </div>
          ))}
        </div>
      </Card>
    </section>
  );
};

export default PropertyTimeline;
